import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Loader2, RotateCcw } from 'lucide-react';
import CandidateProfileView from '../../Candidate/Components/CandidateProfileView';
import { CandidateProfile } from '../../Candidate/Types/CandidateType';
import { getCandidateProfileById } from '../../Candidate/Services/CandidateService';
import { getJobTemplateById } from '../../JobPostTemplate/Services/JobPostTemplateService';
import { JobTemplate } from '../../JobPostTemplate/Types/JobPostTemplateType';
import { fetchDataRecord } from '../Services/DataService';
import { DataQueryResult, ResultColumn } from '../Types/DataServiceType';
import DynamicRecordView from '../Components/DataService/DynamicRecordView';
import JobTemplateRecordView from '../Components/DataService/JobTemplateRecordView';
import { normalizeTable, parseRecordRef, recordKind } from '../Components/DataService/recordLink';

const labelClass = 'text-[10px] font-black uppercase tracking-widest text-slate-500';

/**
 * One row, opened in its own tab, rendered as a real screen.
 *
 * <p>The row is re-read from the server rather than carried along in the URL.
 * That keeps a bookmark working, but more importantly it re-checks the
 * caller's table allowlist when the record is opened, so narrowing someone's
 * configuration takes effect immediately rather than at whatever moment the
 * link was built.</p>
 */
const DataServiceRecordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const ref = useMemo(() => parseRecordRef(searchParams.toString()), [searchParams]);

  const [result, setResult] = useState<DataQueryResult | null>(null);
  const [candidate, setCandidate] = useState<CandidateProfile | null>(null);
  const [job, setJob] = useState<JobTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!ref) {
      setError('This link is missing the record it points at.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    setCandidate(null);
    setJob(null);

    const response = await fetchDataRecord(ref.connectionId, {
      tableName: ref.table,
      key: ref.primaryKey,
    });

    if (!response?.success) {
      setError(response?.message || 'This record could not be read.');
      setLoading(false);
      return;
    }

    const data = response.data;
    setResult(data);

    if (!data || data.rows.length === 0) {
      setError('That row no longer exists, or the key in this link does not match it.');
      setLoading(false);
      return;
    }

    if (data.rows.length > 1) {
      setError('That key matches more than one row, so it does not identify a single record.');
      setLoading(false);
      return;
    }

    const keyValue = Object.values(ref.primaryKey)[0];
    const kind = recordKind(ref.table);

    if (kind === 'candidate' && keyValue) {
      const profile = await getCandidateProfileById(keyValue);
      if (profile?.success) setCandidate(profile.data);
    }

    if (kind === 'job-template' && keyValue) {
      const template = await getJobTemplateById(keyValue);
      if (template?.success) setJob(template.data);
    }

    setLoading(false);
  }, [ref]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ResultColumn[] = result?.columns ?? [];
  const row = result?.rows[0] ?? [];
  const kind = ref ? recordKind(ref.table) : 'generic';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/40 px-6 py-4">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <a
              href="-1"
              onClick={(event) => {
                event.preventDefault();
                window.close();
              }}
              className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-slate-500 transition hover:text-cyan-400"
            >
              <ArrowLeft size={13} /> Back
            </a>
            <h1 className="mt-1 truncate text-lg font-black tracking-tight text-slate-50">
              {ref ? normalizeTable(ref.table) : 'Record'}
            </h1>
            <p className={`mt-0.5 ${labelClass}`}>
              {ref ? result?.connectionAlias ?? 'Record detail' : 'No record selected'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40"
          >
            <RotateCcw size={13} /> Refresh
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {loading && (
          <div className="flex items-center justify-center gap-3 py-24 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className={`${labelClass}`}>Loading record</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex items-start gap-3 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-5 py-4">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-rose-400" />
            <div>
              <p className="text-sm font-bold text-rose-200">Cannot show this record</p>
              <p className="mt-1 text-xs text-rose-300/80">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && ref && (
          <div className="space-y-8">
            {kind === 'candidate' && candidate && <CandidateProfileView data={candidate} />}

            {kind === 'job-template' && job && <JobTemplateRecordView job={job} />}

            {(kind === 'generic' ||
              (kind === 'candidate' && !candidate) ||
              (kind === 'job-template' && !job)) && (
              <DynamicRecordView
                columns={columns}
                row={row}
                tableLabel={ref.table}
              />
            )}

            {(kind === 'candidate' || kind === 'job-template') && (
              <details className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-3">
                <summary className={`cursor-pointer ${labelClass}`}>
                  Source row from {ref.table}
                </summary>
                <div className="mt-4">
                  <DynamicRecordView columns={columns} row={row} tableLabel={ref.table} />
                </div>
              </details>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default DataServiceRecordPage;
